import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';

/**
 * Notification Center data layer. Query keys are all prefixed with
 * 'notifications' so the SSE live-update handler in AppShell invalidates them
 * on `notification.created`.
 */

export interface Notif {
  id: string;
  type: string;
  category: string;
  reason: string | null;
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  body: string | null;
  link: string | null;
  entityType: string | null;
  entityId: string | null;
  ticketId: string | null;
  ticketCode: string | null;
  ticketTitle?: string | null;
  commentId: string | null;
  actor: { id: string | null; name: string | null } | null;
  metadata: Record<string, unknown>;
  read: boolean;
  readAt: string | null;
  archived: boolean;
  archivedAt: string | null;
  createdAt: string;
}

export interface NotifPage {
  unread: number;
  items: Notif[];
  pagination: { page: number; limit: number; total: number; hasMore: boolean };
}

export interface NotifCounts {
  totalUnread: number;
  all: number;
  mentions: number;
  assigned: number;
  watching: number;
  ticketUpdates: number;
  prDevelopment: number;
  system: number;
}

export type CategoryFilter =
  | 'ALL'
  | 'MENTION'
  | 'ASSIGNED'
  | 'WATCHING'
  | 'TICKET_UPDATE'
  | 'PR_DEVELOPMENT'
  | 'SYSTEM';

export interface ListParams {
  category?: CategoryFilter;
  type?: string;
  status?: 'all' | 'unread' | 'read' | 'archived';
  search?: string;
  ticketKey?: string;
  actorUserId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sort?: 'newest' | 'oldest';
}

function listPath(p: ListParams): string {
  return `/v2/notifications${qs({
    category: p.category && p.category !== 'ALL' ? p.category : undefined,
    type: p.type,
    status: p.status,
    search: p.search,
    ticketKey: p.ticketKey,
    actorUserId: p.actorUserId,
    dateFrom: p.dateFrom,
    dateTo: p.dateTo,
    page: p.page,
    limit: p.limit,
    sort: p.sort,
  })}`;
}

export function useNotifications(params: ListParams, opts?: { refetchInterval?: number; enabled?: boolean }) {
  return useQuery({
    queryKey: ['notifications', 'list', params],
    queryFn: () => api.get<NotifPage>(listPath(params)),
    refetchInterval: opts?.refetchInterval,
    enabled: opts?.enabled,
  });
}

export function useNotificationCounts() {
  return useQuery({
    queryKey: ['notifications', 'counts'],
    queryFn: () => api.get<NotifCounts>('/v2/notifications/counts'),
    refetchInterval: 30_000,
  });
}

export interface UnreadByUser { userId: string; name: string; email: string; unread: number }

/** Team view: how many unread notifications each person still has. */
export function useUnreadByUser(enabled = true) {
  return useQuery({
    queryKey: ['notifications', 'unread-by-user'],
    queryFn: () => api.get<{ items: UnreadByUser[] }>('/v2/notifications/unread-by-user'),
    enabled,
    refetchInterval: 60_000,
  });
}

/** Mutations that all invalidate the notification query family on success. */
export function useNotificationActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({ mutationFn: (id: string) => api.post(`/v2/notifications/${id}/read`), onSuccess: invalidate });
  const markUnread = useMutation({ mutationFn: (id: string) => api.post(`/v2/notifications/${id}/unread`), onSuccess: invalidate });
  const archive = useMutation({ mutationFn: (id: string) => api.post(`/v2/notifications/${id}/archive`), onSuccess: invalidate });
  const unarchive = useMutation({ mutationFn: (id: string) => api.post(`/v2/notifications/${id}/unarchive`), onSuccess: invalidate });
  const markAllRead = useMutation({
    mutationFn: (category?: CategoryFilter) =>
      api.post('/v2/notifications/read-all', category && category !== 'ALL' ? { category } : {}),
    onSuccess: invalidate,
  });
  // Mark every notification for one ticket as read ("I've read all of these").
  const markTicketRead = useMutation({
    mutationFn: (ticketId: string) => api.post<{ count: number }>(`/v2/notifications/ticket/${ticketId}/read`),
    onSuccess: invalidate,
  });

  return { markRead, markUnread, archive, unarchive, markAllRead, markTicketRead };
}

/** Where a notification click should navigate (deep-links to comment/PR when known). */
export function notifHref(n: Notif): string | null {
  if (n.link) return n.link;
  if (n.ticketId) {
    if (n.commentId) return `/tickets/${n.ticketId}?commentId=${n.commentId}`;
    if (n.category === 'PR_DEVELOPMENT') return `/tickets/${n.ticketId}?tab=development`;
    return `/tickets/${n.ticketId}`;
  }
  return null;
}
